import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Row,
  Col,
  Input,
  Dropdown,
  Pagination,
  Alert,
  Select,
  Space,
  Form,
  InputNumber,
  DatePicker,
  Typography,
  Card,
  Divider,
  Tooltip,
  ConfigProvider,
  theme
} from "antd";
import {
  SearchOutlined,
  FileExcelOutlined,
  TableOutlined,
  ClearOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
  FilterOutlined,
  EditOutlined,
  FileAddOutlined
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { getNumericFilters, getStringFilters, getDateFilters, applyAdvancedFilter, exportToExcel } from "../utils/tableFilters";
import { trIncludes } from "../utils/trText";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const ListPage = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [data, setData] = useState([]);
  const navigate = useNavigate();
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});
  const [searchText, setSearchText] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showData, setShowData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [alert, setAlert] = useState({ show: false, message: "", type: "success" });
  const [form] = Form.useForm();
  const { darkMode } = useTheme();

    useEffect(() => {
        fetch("data.json")
        .then((res) => res.json())
        .then((data) => {
            const cleanedData = data.map((item, index) => ({
            ...item,
            key: index,
            }));
            setData(cleanedData);
            setShowData(cleanedData);

            if (data.length > 0) {
            const sample = data[0];
            const dynamicCols = Object.keys(sample)
                .filter((key) => key !== "Değişen Kabin")
                .map((key) => {
                const firstValue = sample[key];
                const isDateColumn = key === "Taşıma Tarihi";
                const isNumber = typeof firstValue === "number" && !isNaN(firstValue);
                const uniqueValues = [...new Set(data.map((item) => item[key]))].filter(
                    (val) => val !== undefined && val !== null && val !== ""
                );

                let filters = [];
                if (isNumber) {
                    filters = getNumericFilters();
                } else if (isDateColumn) {
                    filters = getDateFilters();
                } else {
                    filters = getStringFilters();
                }

                return {
                    key: key,
                    title: key === "key_kabin_islem" ? "İşlem" : key,
                    dataIndex: key,
                    isDateColumn,
                    isNumber,
                    isString: !isNumber && !isDateColumn,
                    uniqueValues,
                    sortable: true,
                    filters, 
                };
                });
            setColumns(dynamicCols);
            }
        })
        .catch((error) => {
            console.error("Error loading data:", error);
        });
    }, []);

    const handleSort = (columnKey) => {
        const column = columns.find((col) => col.key === columnKey);
        if (!column) return;

        let newOrder = "asc";
        if (sortedInfo.columnKey === columnKey) {
          if (sortedInfo.order === "asc") {
              newOrder = "desc";
          } else if (sortedInfo.order === "desc") {
              newOrder = null;
          }
        }

        setSortedInfo({
          columnKey: newOrder ? columnKey : null,
          order: newOrder,
        });
    };

    const handleSearch = (e) => {
        setSearchText(e.target.value);
        setCurrentPage(1);
    };

    const handleFilterChange = (columnKey, valueType, value) => {
        setFilteredInfo((prev) => ({
        ...prev,
        [columnKey]: {
            ...prev[columnKey],
            [valueType]: value,
        },
        }));
        setCurrentPage(1);
    };

    const clearColumnFilter = (columnKey) => {
        setFilteredInfo((prev) => {
        const newFilteredInfo = { ...prev };
        delete newFilteredInfo[columnKey];
        return newFilteredInfo;
        });
        setCurrentPage(1);
    };

    const filteredAndSortedData = useMemo(() => {
        let result = data.filter((item) => {
          const matchesSearch = Object.values(item).some((val) =>
              trIncludes(String(val), searchText)
          );

          const matchesFilters = Object.entries(filteredInfo).every(([key, filter]) => {
              const recordValue = item[key];
              const col = columns.find((c) => c.key === key);
              return applyAdvancedFilter(recordValue, filter, col);
          });

          return matchesSearch && matchesFilters;
        });

        if (sortedInfo.columnKey && sortedInfo.order) {
          const column = columns.find((col) => col.key === sortedInfo.columnKey);
          if (column) {
              result.sort((a, b) => {
              const valA = a[sortedInfo.columnKey];
              const valB = b[sortedInfo.columnKey];

              if (column.isDateColumn) {
                  const dateA = valA ? moment(valA) : null;
                  const dateB = valB ? moment(valB) : null;
                  if (!dateA && !dateB) return 0;
                  if (!dateA) return sortedInfo.order === "asc" ? -1 : 1;
                  if (!dateB) return sortedInfo.order === "asc" ? 1 : -1;
                  const comparison = dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
                  return sortedInfo.order === "asc" ? comparison : -comparison;
              } else if (column.isNumber) {
                  const numA = Number(valA) || 0;
                  const numB = Number(valB) || 0;
                  return sortedInfo.order === "asc" ? numA - numB : numB - numA;
              } else {
                  const strA = String(valA || "").toLowerCase();
                  const strB = String(valB || "").toLowerCase();
                  const comparison = strA.localeCompare(strB, 'tr');
                  return sortedInfo.order === "asc" ? comparison : -comparison;
              }
              });
          }
        }

        return result;
    }, [data, searchText, filteredInfo, sortedInfo, columns]);

    const paginatedData = useMemo(() => {
      const startIndex = (currentPage - 1) * rowsPerPage;
      return filteredAndSortedData.slice(startIndex, startIndex + rowsPerPage);
    }, [filteredAndSortedData, currentPage, rowsPerPage]);

    const totalPages = Math.ceil(filteredAndSortedData.length / rowsPerPage);

    const handleEdit = (row) => {
      setSelectedRow(row);
      setModalVisible(true);
    };

    const handleModalOk = () => {
      setModalVisible(false);
      setAlert({ show: true, message: "Değişiklikler kaydedildi", type: "success" });
      setTimeout(() => setAlert({ show: false, message: "", type: "success" }), 3000);
    };

    const handleModalCancel = () => {
      setModalVisible(false);
    };

    const stripHtml = (html) => {
      if (!html) return "";
      const doc = new DOMParser().parseFromString(html, "text/html");
      return doc.body.textContent || "";
    };

    const clearAllFilters = useCallback(() => {
      setFilteredInfo({});
      setSearchText("");
      setSortedInfo({});
      setCurrentPage(1);
    }, []);

    const pivotTable = useCallback(() => {
      navigate("/pivot");
    }, [navigate]);

    const addNewRecord = useCallback(() => {
      navigate("/formAddPlatform");
    }, [navigate]);

    const getSortIcon = (columnKey) => {
      if (sortedInfo.columnKey === columnKey) {
        return sortedInfo.order === "asc" ? <CaretUpOutlined /> : <CaretDownOutlined />;
      }
      return null;
    };

    const removeColumn = (columnKey) => {
      setColumns((prevCols) => prevCols.filter((col) => col.key !== columnKey));
    };

  const renderTableCell = (item, column) => {
    if (column.key === "Açıklama") {
      return <span>{stripHtml(item[column.key])}</span>;
    }
    if (column.key === "key_kabin_islem") {
      return (
        <Button
          onClick={() => handleEdit(item)}
          size="small"
          type="primary"
          icon={<EditOutlined />}
          className={darkMode ? "btn-edit-dark" : "btn-edit-light"}              
        >
          Düzenle
        </Button>
      );
    }
    return item[column.key];
  };

  const getFilterDropdown = (column) => {
    const currentFilter = filteredInfo[column.key] || {};
    const condition = currentFilter.condition || "choose";

    return (
      <div style={{ padding: 16, minWidth: 'min(300px, 85vw)' }}>
        <Space direction="vertical" style={{ width: '100%', backgroundColor: 'var(--color-bg)', border: `1px solid ${'var(--color-theme)'}`, padding: 12}}>
          <div>
            <Text strong style={{ fontSize: '12px', color: 'var(--color-theme)', borderColor: 'var(--color-theme)' }}>
              Filtre Şartı
            </Text>
            <Select
              size="small"
              value={condition}
              onChange={(value) => handleFilterChange(column.key, "condition", value)}
              style={{ width: '100%' }}
            >
              {column.filters.map((filter) => (
                <Option key={filter.value} value={filter.value}>
                  {filter.text}
                </Option>
              ))}
            </Select>
          </div>

          {/* String değer seçimi */}
          {column.isString && condition === "select" && (
            <div>
              <Text strong style={{ fontSize: '12px', color: 'var(--color-theme)' }}>
                Değer Seç
              </Text>
              <Select
                size="small"
                value={currentFilter.value || ""}
                onChange={(value) => handleFilterChange(column.key, "value", value)}
                style={{ width: '100%' }}
                placeholder="Seçiniz"
              >
                <Option value="">Seçiniz</Option>
                {column.uniqueValues.map((val) => (
                  <Option key={val} value={val}>
                    {val}
                  </Option>
                ))}
              </Select>
            </div>
          )}

          {/* Sayısal filtreler için input alanları */}
          {column.isNumber && condition !== "choose" && condition !== "between" && (
            <div>
              <Text strong style={{ fontSize: '12px', color: 'var(--color-theme)' }}>
                Değer
              </Text>
              <InputNumber
                size="small"
                placeholder="Değer giriniz"
                value={currentFilter.value || ""}
                onChange={(value) => handleFilterChange(column.key, "value", value)}
                style={{ width: '100%' }}
              />
            </div>
          )}

          {/* Sayısal arasında filtresi */}
          {column.isNumber && condition === "between" && (
            <>
              <div>
                <Text strong style={{ fontSize: '12px', color: 'var(--color-theme)' }}>
                  Min Değer
                </Text>
                <InputNumber
                  size="small"
                  placeholder="Min"
                  value={currentFilter.min || ""}
                  onChange={(value) => handleFilterChange(column.key, "min", value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <Text strong style={{ fontSize: '12px', color: 'var(--color-theme)' }}>
                  Max Değer
                </Text>
                <InputNumber
                  size="small"
                  placeholder="Max"
                  value={currentFilter.max || ""}
                  onChange={(value) => handleFilterChange(column.key, "max", value)}
                  style={{ width: '100%' }}
                />
              </div>
            </>
          )}

          {/* String filtreler için input alanları */}
          {column.isString && !["choose", "empty", "notEmpty", "select"].includes(condition) && (
            <div>
              <Text strong style={{ fontSize: '12px', color: 'var(--color-theme)' }}>
                Değer
              </Text>
              <Input
                size="small"
                placeholder="Değer giriniz"
                value={currentFilter.value || ""}
                onChange={(e) => handleFilterChange(column.key, "value", e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
          )}

          {/* Tarih filtreler için input alanları */}
          {column.isDateColumn && ["equals", "before", "after"].includes(condition) && (
            <div>
              <Text strong style={{ fontSize: '12px', color: 'var(--color-theme)' }}>
                Tarih
              </Text>
              <DatePicker
                size="small"
                value={currentFilter.value ? moment(currentFilter.value) : null}
                onChange={(date) => handleFilterChange(column.key, "value", date ? date.format('YYYY-MM-DD') : null)}
                style={{ width: '100%' }}
                format="DD-MM-YYYY"
              />
            </div>
          )}

          {/* Tarih arasında filtresi */}
          {column.isDateColumn && condition === "between" && (
            <>
              <div>
                <Text strong style={{ fontSize: '12px', color: 'var(--color-theme)' }}>
                  Başlangıç Tarihi
                </Text>
                <DatePicker
                  size="small"
                  value={currentFilter.startDate ? moment(currentFilter.startDate) : null}
                  onChange={(date) => handleFilterChange(column.key, "startDate", date ? date.format('YYYY-MM-DD') : null)}
                  style={{ width: '100%' }}
                  format="DD-MM-YYYY"
                />
              </div>
              <div>
                <Text strong style={{ fontSize: '12px', color: 'var(--color-theme)' }}>
                  Bitiş Tarihi
                </Text>
                <DatePicker
                  size="small"
                  value={currentFilter.endDate ? moment(currentFilter.endDate) : null}
                  onChange={(date) => handleFilterChange(column.key, "endDate", date ? date.format('YYYY-MM-DD') : null)}
                  style={{ width: '100%' }}
                  format="DD-MM-YYYY"
                />
              </div>
            </>
          )}

          <Button
            size="small"
            onClick={() => clearColumnFilter(column.key)}
            style={{ marginTop: 8, width: '100%', borderColor: 'var(--color-theme)', color: 'var(--color-theme)' }}
          >
            Filtreyi Sıfırla
          </Button>
        </Space>
      </div>
    );
  };

  const antColumns = columns.map((column) => ({
    title: (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Tooltip title="Sütunu kaldır">
          <span
            style={{ cursor: "pointer", color: darkMode ? 'var(--color-bg)' : 'var(--color-theme)', fontWeight: 'bold' }}
            onClick={() => removeColumn(column.key)}
          >
            {column.title}
          </span>
        </Tooltip>
        {column.sortable && (
          <Button
            type="text"
            size="small"
            onClick={() => handleSort(column.key)}
            style={{ marginLeft: 4 }}
          >
            {getSortIcon(column.key) || <CaretUpOutlined style={{ opacity: 0.3 }} />}
          </Button>
        )}
        <Dropdown
          overlay={getFilterDropdown(column)}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button 
            type="text" 
            size="small" 
            style={{ 
              marginLeft: 4,
              color: filteredInfo[column.key] ? 'var(--color-theme)' : '#ccc'
            }}
          >
            <FilterOutlined style={{ fontSize: 12 }} />
          </Button>
        </Dropdown>
      </div>
    ),
    dataIndex: column.key,
    key: column.key,
    align: 'center',
    render: (text, record) => renderTableCell(record, column),
  }));

  return (
    <div style={{ backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : 'var(--color-accent)', minHeight: '100vh' }}>
      <Card
        style={{borderRadius: '0px'}}
      >
        {alert.show && (
          <Alert
            message={alert.message}
            type={alert.type}
            showIcon
            closable
            onClose={() => setAlert({ show: false, message: "", type: "success" })}
            style={{ marginBottom: 16 }}
          />
        )}

        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={12}>
            <Space wrap>
                <Text>Satır Sayısı:</Text>
                <Select
                    value={rowsPerPage}
                    onChange={(value) => {
                    setRowsPerPage(value);
                    setCurrentPage(1);
                    }}
                    style={{ width: 80, color: 'var(--color-theme)' }}
                >
                    <Option value={10}>10</Option>
                    <Option value={25}>25</Option>
                    <Option value={50}>50</Option>
                </Select>
                <Button 
                    onClick={clearAllFilters} 
                    icon={<ClearOutlined/>}
                    type="primary"
                    ghost
                    className={darkMode ? "btn-filter-dark" : "btn-filter-light"}              
                >
                    Filtreleri Temizle
                </Button>
            </Space>
          </Col>

          <Col xs={24} md={12}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
              <Input
                placeholder="Arama..."
                value={searchText}
                onChange={handleSearch}
                prefix={<SearchOutlined />}
                style={{ width: 250, maxWidth: '100%', borderColor: 'var(--color-theme)' }}
              />
              <Button 
                onClick={() => exportToExcel(filteredAndSortedData)} 
                icon={<FileExcelOutlined />}
                type="primary"
                ghost
                className={darkMode ? "btn-excel-dark" : "btn-excel-light"}              
              >
                Excel'e Aktar
              </Button>
              <Button
                onClick={pivotTable}
                icon={<TableOutlined />}
                ghost
                className={darkMode ? "btn-pivot-dark" : "btn-pivot-light"}              
              >
                Pivot Tablo
              </Button>

              <Button
                onClick={addNewRecord}
                icon={<FileAddOutlined />}
                className={darkMode ? "btn-save-dark" : "btn-save-light"}
              >
                Yeni Kayıt Ekle
              </Button>
            </div>
          </Col>
        </Row>

        <Table
          columns={antColumns}
          dataSource={paginatedData}
          pagination={false}
          size="small"
          bordered
          scroll={{ x: 'max-content' }}
          style={{ marginBottom: 16, marginTop: 16 }}
        />
        <Row justify="space-between" align="middle">
          <Col>
            <Text type="secondary">
              Toplam {filteredAndSortedData.length} kayıt (
              {((currentPage - 1) * rowsPerPage) + 1}-
              {Math.min(currentPage * rowsPerPage, filteredAndSortedData.length)})
            </Text>
          </Col>
          <Col>
            <Pagination
              current={currentPage}
              total={filteredAndSortedData.length}
              pageSize={rowsPerPage}
              onChange={(page) => setCurrentPage(page)}
              style={{color: 'var(--color-theme)'}}
              showSizeChanger={false}
              showQuickJumper
              showTotal={(total, range) => 
                `${range[0]}-${range[1]} / ${total} kayıt`
              }
            />
          </Col>
        </Row>
      </Card>

      <Modal
        title="Satırı Düzenle"
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Kaydet"
        cancelText="Vazgeç"
        width={600}
        backgroundColor={'var(--dark-success)'}
        okButtonProps={{ 
            className: darkMode ? "btn-apply-dark" : "btn-apply-light"
        }}
        cancelButtonProps={{ 
            className: darkMode ? "btn-cancel-dark" : "btn-cancel-light"
         }}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Nereye" name="nereye">
            <Input defaultValue={selectedRow?.["Nereye"] || ""} />
          </Form.Item>

          <Form.Item label="Taşıma Tarihi" name="tasima_tarihi">
            <DatePicker
              value={selectedDate ? dayjs(selectedDate) : null}
              onChange={(date) => setSelectedDate(date ? date.toISOString() : null)}
            />
          </Form.Item>

          <Form.Item label="Sebep" name="sebep">
            <Select defaultValue={selectedRow?.["Sebep"] || ""}>
              <Option value="İade">İade</Option>
              <Option value="Sevk">Sevk</Option>
            </Select>
          </Form.Item>

          <Form.Item label="Açıklama" name="aciklama">
            <Input.TextArea
              rows={4}
              defaultValue={stripHtml(selectedRow?.["Açıklama"] || "")}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ListPage;